import type { ReactiveSet } from '@solid-primitives/set'
import { Title } from '@solidjs/meta'
import { type Component, createEffect, createMemo, createSignal, Show } from 'solid-js'
import { createStore } from 'solid-js/store'
import Button from '~/components/Button'
import ButtonGroup from '~/components/ButtonGroup'
import Card from '~/components/Card'
import Checkbox from '~/components/Checkbox'
import Chip from '~/components/Chip'
import FabMenu from '~/components/FabMenu'
import InfiniteList from '~/components/InfiniteList'
import Input from '~/components/Input'
import Menu from '~/components/Menu'
import Select from '~/components/Select'
import SwipeAction from '~/components/SwipeAction'
import Table from '~/components/Table'
import useAlert from '~/components/useAlert'
import { useToast } from '~/components/useToast'
import { useCompanySettings } from '~/hooks/useCompanySettings'
import { useDebouncedSignal } from '~/hooks/useDebouncedSignal'
import { useIsMobile } from '~/hooks/useIsMobile'
import { usePermissions } from '~/hooks/usePermissions'
import { useServerTable } from '~/hooks/useServerTable'
import TableFilters, { FiltersButton } from '~/layers/TableFilters'
import { type Path, useNavigate } from '~/router'
import { type PaymentAccountType, paymentAccountService } from '~/services/payment-account.service'
import { resolveError } from '~/utils/error'
import { P } from '~/utils/permissions'
import { formatDate, formatMoney } from '~/utils/settings'

const statusColors: Record<string, 'default' | 'success' | 'warning' | 'danger' | 'info'> = {
  draft: 'default',
  sent: 'info',
  paid: 'success',
  overdue: 'danger'
}

const statusLabels: Record<string, string> = {
  draft: 'Borrador',
  sent: 'Enviada',
  paid: 'Pagada',
  overdue: 'Vencida'
}

const PaymentAccountsPage: Component = () => {
  const toast = useToast()
  const alert = useAlert()
  const navigate = useNavigate()
  const { can } = usePermissions()
  const { settings } = useCompanySettings()
  const isMobile = useIsMobile()

  // Búsqueda y filtros server-side
  const [filtersOpen, setFiltersOpen] = createSignal(false)
  const [filterStatus, setFilterStatus] = createSignal('')
  const [filterSearch, debouncedSearch, setFilterSearch] = useDebouncedSignal('')

  // Filtros compartidos entre la tabla (desktop) y el scroll infinito (móvil).
  const buildParams = (): Record<string, string | number | undefined> => {
    const q = debouncedSearch().trim()
    const p: Record<string, string | number | undefined> = {}
    if (q) p.title = `%${q}%`
    if (filterStatus()) p.status = filterStatus()
    return p
  }

  const table = useServerTable<PaymentAccountType>({
    fetcher: params => paymentAccountService.list(params as Parameters<typeof paymentAccountService.list>[0]),
    params: buildParams
  })

  // ── Scroll infinito móvil ────────────────────────────────────────────────
  const [mobileItems, setMobileItems] = createStore<PaymentAccountType[]>([])
  const [mobileLoading, setMobileLoading] = createSignal(false)
  const [mobileTotal, setMobileTotal] = createSignal(0)
  const hasMore = () => mobileItems.length < mobileTotal()

  const mobileFetch = async (reset: boolean) => {
    if (!reset && mobileLoading()) return
    setMobileLoading(true)
    try {
      const page = reset ? 1 : Math.floor(mobileItems.length / table.perPage()) + 1
      const res = await paymentAccountService.list({ ...buildParams(), page, limit: table.perPage() })
      setMobileTotal(res.meta.total)
      setMobileItems(reset ? res.items : prev => [...prev, ...res.items])
    } catch {
      // error silencioso: el sentinela reintentará en el siguiente scroll
    } finally {
      setMobileLoading(false)
    }
  }

  // Reinicia la lista (y recarga desde la página 1) cuando cambian los filtros.
  // Al entrar en móvil la clave pasa de '' a los filtros actuales, así que este
  // mismo efecto hace la primera carga sin duplicar peticiones.
  const [filtersKey, setFiltersKey] = createSignal('')
  createEffect(() => {
    if (!isMobile()) return
    const key = JSON.stringify([filterStatus(), debouncedSearch()])
    if (key === filtersKey()) return
    setFiltersKey(key)
    void mobileFetch(true)
  })

  const loadMore = () => {
    if (!hasMore() || mobileLoading()) return
    void mobileFetch(false)
  }

  const activeFilters = createMemo(() => [filterStatus(), filterSearch()].filter(v => v !== '').length)

  const clearFilters = () => {
    setFilterStatus('')
    setFilterSearch('')
  }

  const [pdfBusyId, setPdfBusyId] = createSignal<number | null>(null)

  const handleDelete = async (account: PaymentAccountType) => {
    const ok = await alert.confirmDelete({
      title: 'Eliminar cuenta de cobro',
      message: `Se eliminará la cuenta ${account.number} y sus conceptos.`
    })
    if (!ok) return

    try {
      await paymentAccountService.delete(account.id)
      toast.addToast({ title: 'Cuenta de cobro eliminada', type: 'success' })
      // En la vista móvil la lista se acumula en `mobileItems`: quitarla también
      // de ahí, o el `refetch` (misma página) no la actualizaría.
      setMobileItems(prev => prev.filter(i => i.id !== account.id))
      await table.refetch()
    } catch (err: unknown) {
      toast.addToast({ title: resolveError(err), type: 'danger' })
    }
  }

  // ── Selección masiva ────────────────────────────────────────────────────
  const [selectedIds, setSelectedIds] = createSignal<ReadonlySet<number>>(new Set())

  const handleSelectionChange = (selected: ReactiveSet<unknown>) => {
    const next = new Set<number>()
    for (const value of selected.values()) next.add(value as number)
    setSelectedIds(next)
  }

  const toggleSelect = (id: number) => {
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const handleBulkDelete = async () => {
    const ids = [...selectedIds()]
    if (ids.length === 0) return
    const ok = await alert.confirmDelete({
      title: 'Eliminar cuentas de cobro',
      message: `¿Eliminar ${ids.length} ${ids.length === 1 ? 'cuenta' : 'cuentas'} de cobro y sus conceptos? Esta acción no se puede deshacer.`
    })
    if (!ok) return
    try {
      const res = await paymentAccountService.bulkDelete(ids)
      toast.addToast({ title: `${res.deleted} ${res.deleted === 1 ? 'cuenta eliminada' : 'cuentas eliminadas'}`, type: 'success' })
      setSelectedIds(new Set<number>())
      setMobileItems(prev => prev.filter(i => !ids.includes(i.id)))
      await table.refetch()
    } catch (err: unknown) {
      toast.addToast({ title: resolveError(err), type: 'danger' })
    }
  }

  const handlePdf = async (account: PaymentAccountType) => {
    setPdfBusyId(account.id)
    try {
      await paymentAccountService.openPdf(account.id)
    } catch (err: unknown) {
      toast.addToast({ title: resolveError(err), type: 'danger' })
    } finally {
      setPdfBusyId(null)
    }
  }

  /** Descarga el archivo en vez de abrirlo en otra pestaña. */
  const handleDownload = async (account: PaymentAccountType) => {
    setPdfBusyId(account.id)
    try {
      await paymentAccountService.downloadPdf(account.id, account.number)
    } catch (err: unknown) {
      toast.addToast({ title: resolveError(err), type: 'danger' })
    } finally {
      setPdfBusyId(null)
    }
  }

  const accountChips = createMemo(() => [
    ...(filterSearch() ? [{ label: `«${filterSearch()}»`, color: 'info' as const, icon: 'material-symbols-light:search', onRemove: () => setFilterSearch('') }] : []),
    ...(filterStatus() ? [{ label: statusLabels[filterStatus()] ?? filterStatus(), color: 'primary' as const, onRemove: () => setFilterStatus('') }] : [])
  ])

  const columns = [
    { header: 'Número', accessor: 'number' as const, sticky: 'left' as const, minWidth: 150 },
    {
      header: 'Concepto',
      accessor: (a: PaymentAccountType) => (
        <div>
          <div class="font-medium text-foreground">{a.title}</div>
          <div class="text-xs text-foreground-subtle">{a.items?.length ?? 0} concepto(s)</div>
        </div>
      )
    },
    { header: 'Cliente', accessor: (a: PaymentAccountType) => a.customer?.name ?? '-' },
    {
      header: 'Total',
      accessor: (a: PaymentAccountType) => formatMoney(a.total, a.currency, settings()?.currencies ?? [])
    },
    {
      header: 'Vence',
      accessor: (a: PaymentAccountType) => (a.dueDate ? formatDate(a.dueDate, settings()?.locale) : '-')
    },
    {
      header: 'Estado',
      accessor: (a: PaymentAccountType) => (
        <Chip color={statusColors[a.status]} variant="soft" size="xs">
          {statusLabels[a.status]}
        </Chip>
      )
    }
  ]

  return (
    <>
      <Title>Cuentas de Cobro | Florez Jaramillo</Title>
      <Show when={table.error()}>
        <div class="rounded-xl border border-danger-soft bg-danger-soft/30 px-4 py-3 text-sm text-danger mb-4">{resolveError(table.error())}</div>
      </Show>

      <Show when={!isMobile()}>
        <Table<PaymentAccountType>
          title="Cuentas de Cobro"
          subtitle="Documentos de cobro, su PDF con datos de pago y su estado"
          headerActions={
            <div class="hidden sm:flex items-center gap-2">
              <FiltersButton activeCount={activeFilters()} onClick={() => setFiltersOpen(true)} />
              <Show when={can(P.paymentAccounts.create)}>
                <Button onClick={() => navigate('/admin/cuentas-cobro/nueva')} prependIcon="material-symbols-light:add-circle-outline">
                  Nueva Cuenta de Cobro
                </Button>
              </Show>
            </div>
          }
          data={table.data()}
          columns={columns}
          keyField="id"
          headSticky
          loading={table.loading()}
          serverSide
          selectable={can(P.paymentAccounts.delete)}
          selectableColor="danger"
          onSelectionChange={handleSelectionChange}
          onDeleteSelected={() => void handleBulkDelete()}
          total={table.total()}
          page={table.page()}
          perPage={table.perPage()}
          onPageChange={table.onPageChange}
          onPerPageChange={table.onPerPageChange}
          emptyMessage="Aún no has creado cuentas de cobro"
          actions={account => (
            <ButtonGroup size="sm" variant="soft">
              <Menu
                trigger={<Button color="info" icon="material-symbols-light:picture-as-pdf-outline" disabled={pdfBusyId() === account.id} tooltip="Acciones PDF" aria-label="Acciones PDF" />}
                items={[
                  { label: 'Ver PDF', icon: 'material-symbols-light:visibility-outline', onClick: () => handlePdf(account) },
                  { label: 'Descargar PDF', icon: 'material-symbols-light:download', onClick: () => handleDownload(account) }
                ]}
              />
              <Show when={can(P.paymentAccounts.update)}>
                <Button size="sm" variant="soft" color="warning" onClick={() => navigate(`/admin/cuentas-cobro/${account.id}` as Path)} tooltip="Editar" icon="material-symbols-light:edit-outline" />
              </Show>
              <Show when={can(P.paymentAccounts.delete)}>
                <Button size="sm" variant="soft" color="danger" onClick={() => handleDelete(account)} tooltip="Eliminar" icon="material-symbols-light:delete-outline" />
              </Show>
            </ButtonGroup>
          )}
        />
      </Show>

      {/* Vista móvil: en pantallas pequeñas la tabla desborda, así que se
          sustituye por tarjetas con acciones deslizables y scroll infinito. */}
      <Show when={isMobile()}>
        <Show when={can(P.paymentAccounts.delete) && selectedIds().size > 0}>
          <div class="mb-3 flex items-center justify-between gap-2 rounded-xl border border-danger-soft bg-danger-soft/30 px-4 py-3">
            <span class="text-sm font-semibold text-danger">
              {selectedIds().size} {selectedIds().size === 1 ? 'seleccionado' : 'seleccionados'}
            </span>
            <Button size="sm" color="danger" variant="soft" icon="material-symbols-light:delete-outline" onClick={() => void handleBulkDelete()}>
              Eliminar
            </Button>
          </div>
        </Show>
        <InfiniteList<PaymentAccountType>
          items={mobileItems}
          keyField={a => a.id}
          hasMore={hasMore()}
          loading={mobileLoading()}
          onLoadMore={loadMore}
          onRefresh={async () => {
            await mobileFetch(true)
          }}
          emptyMessage={<p class="py-10 text-center text-foreground-subtle">Aún no has creado cuentas de cobro</p>}
          endMessage={<span>Has llegado al final de las cuentas de cobro</span>}
          class="space-y-3"
          renderItem={account => (
            <SwipeAction
              itemId={account.id}
              rememberPosition
              onClick={() => navigate(`/admin/cuentas-cobro/${account.id}` as Path)}
              leftOptions={[
                ...(can(P.paymentAccounts.update)
                  ? [
                      {
                        icon: 'material-symbols-light:edit-outline',
                        label: 'Editar',
                        color: 'warning',
                        action: () => navigate(`/admin/cuentas-cobro/${account.id}` as Path)
                      }
                    ]
                  : []),
                {
                  icon: 'material-symbols-light:picture-as-pdf-outline',
                  label: 'PDF',
                  color: 'info',
                  action: () => handlePdf(account)
                }
              ]}
              rightOptions={[
                ...(can(P.paymentAccounts.delete)
                  ? [
                      {
                        icon: 'material-symbols-light:delete-outline',
                        label: 'Eliminar',
                        color: 'danger',
                        action: () => handleDelete(account)
                      }
                    ]
                  : [])
              ]}
            >
              <Card class="!rounded-lg">
                <Card.Body class="flex items-center gap-3">
                  <Show when={can(P.paymentAccounts.delete)}>
                    <Checkbox size="sm" color="danger" checked={selectedIds().has(account.id)} onChange={() => toggleSelect(account.id)} class="shrink-0" />
                  </Show>
                  <div class="min-w-0 flex-1">
                    <div class="flex items-center justify-between gap-2">
                      <p class="font-medium text-foreground truncate">{account.number}</p>
                      <Chip color={statusColors[account.status] ?? 'default'} variant="soft" size="xs" class="shrink-0">
                        {statusLabels[account.status] ?? account.status}
                      </Chip>
                    </div>
                    <p class="text-xs text-foreground-subtle truncate">{account.title}</p>
                    <p class="text-xs text-foreground-subtle truncate">{account.customer?.name ?? '-'}</p>
                    <div class="flex items-center gap-2 mt-1.5">
                      <span class="text-sm font-semibold text-foreground">{formatMoney(account.total, account.currency, settings()?.currencies ?? [])}</span>
                      <Show when={account.dueDate}>
                        <span class="text-xs text-foreground-subtle">Vence {formatDate(account.dueDate, settings()?.locale)}</span>
                      </Show>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </SwipeAction>
          )}
        />
      </Show>

      <TableFilters title="Filtros de cuentas de cobro" isOpen={filtersOpen()} onClose={() => setFiltersOpen(false)} activeCount={activeFilters()} onClear={clearFilters} chips={accountChips()}>
        <Input label="Buscar" value={filterSearch()} onInput={e => setFilterSearch(e.currentTarget.value)} placeholder="Número, concepto o cliente..." startIcon="material-symbols-light:search" />

        <Select
          label="Estado"
          options={[{ value: '', title: 'Cualquier estado' }, ...Object.entries(statusLabels).map(([value, label]) => ({ value, title: label }))]}
          value={filterStatus()}
          onChange={v => setFilterStatus(String(v ?? ''))}
          class="w-full"
        />
      </TableFilters>

      {/* FAB móvil: mismas acciones del header, accesibles en pantallas pequeñas */}
      <div class="sm:hidden">
        <FabMenu
          items={[
            { label: 'Filtros', icon: 'material-symbols-light:tune', onClick: () => setFiltersOpen(true) },
            ...(can(P.paymentAccounts.create)
              ? [{ label: 'Nueva Cuenta de Cobro', icon: 'material-symbols-light:add-circle-outline', onClick: () => navigate('/admin/cuentas-cobro/nueva' as Path) }]
              : [])
          ]}
        />
      </div>

      <alert.AlertComponent />
    </>
  )
}

export default PaymentAccountsPage
